
import React from 'react';

interface InfoModalProps {
  type: 'terms' | 'privacy' | 'how-it-works' | null;
  onClose: () => void;
}

const InfoModal: React.FC<InfoModalProps> = ({ type, onClose }) => {
  if (!type) return null;

  const content = {
    'terms': {
      title: 'Termos de Uso',
      subtitle: 'Regras da nossa vitrine local',
      body: (
        <div className="space-y-6">
          <section>
            <h4 className="font-black text-brand-navy uppercase text-[10px] tracking-widest mb-2">1. A Natureza do Serviço</h4>
            <p className="text-sm text-gray-500 leading-relaxed">O "Compre em Guarulhos" é uma vitrine digital. Não processamos pagamentos nem realizamos entregas. Todas as negociações ocorrem diretamente entre comprador e vendedor via WhatsApp.</p>
          </section>
          <section>
            <h4 className="font-black text-brand-navy uppercase text-[10px] tracking-widest mb-2">2. Responsabilidade do Lojista</h4>
            <p className="text-sm text-gray-500 leading-relaxed">O lojista é o único responsável pela veracidade das informações, preços e entrega dos produtos. É proibido o anúncio de itens ilegais, réplicas não autorizadas ou serviços que violem a legislação brasileira.</p>
          </section>
          <section>
            <h4 className="font-black text-brand-navy uppercase text-[10px] tracking-widest mb-2">3. Uso de Imagens</h4>
            <p className="text-sm text-gray-500 leading-relaxed">Ao publicar fotos, o lojista garante possuir os direitos autorais sobre as mesmas. A plataforma reserva-se o direito de remover anúncios com fotos de baixa qualidade ou inadequadas.</p>
          </section>
        </div>
      )
    },
    'privacy': {
      title: 'Privacidade',
      subtitle: 'Como cuidamos dos seus dados',
      body: (
        <div className="space-y-6">
          <section>
            <h4 className="font-black text-brand-navy uppercase text-[10px] tracking-widest mb-2">1. Coleta de Dados</h4>
            <p className="text-sm text-gray-500 leading-relaxed">Para lojistas, coletamos e-mail, nome e WhatsApp para fins de cadastro e exibição pública. Para compradores, não exigimos cadastro para navegar na vitrine.</p>
          </section>
          <section>
            <h4 className="font-black text-brand-navy uppercase text-[10px] tracking-widest mb-2">2. Exposição Pública</h4>
            <p className="text-sm text-gray-500 leading-relaxed">Ao cadastrar sua loja, seu número de WhatsApp e localização (bairro) tornam-se públicos para permitir que clientes entrem em contato.</p>
          </section>
          <section>
            <h4 className="font-black text-brand-navy uppercase text-[10px] tracking-widest mb-2">3. Cookies e Analytics</h4>
            <p className="text-sm text-gray-500 leading-relaxed">Utilizamos cookies básicos para melhorar a performance da busca e entender quais bairros e categorias são mais acessados em Guarulhos.</p>
          </section>
        </div>
      )
    },
    'how-it-works': {
      title: 'Como Funciona',
      subtitle: 'O caminho mais curto para o comércio local',
      body: (
        <div className="space-y-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-brand-blue/5 p-8 rounded-[2.5rem] border border-brand-blue/10">
              <span className="text-2xl mb-4 block">🛍️</span>
              <h4 className="font-black text-brand-blue uppercase text-xs tracking-widest mb-3 italic">Para Compradores</h4>
              <ul className="text-sm text-gray-600 space-y-3 font-medium">
                <li className="flex gap-2"><span>1.</span> Explore por bairro ou categoria.</li>
                <li className="flex gap-2"><span>2.</span> Clique na oferta que gostar.</li>
                <li className="flex gap-2"><span>3.</span> Toque no botão WhatsApp para falar direto com o dono da loja.</li>
              </ul>
            </div>
            <div className="bg-brand-orange/5 p-8 rounded-[2.5rem] border border-brand-orange/10">
              <span className="text-2xl mb-4 block">🚀</span>
              <h4 className="font-black text-brand-orange uppercase text-xs tracking-widest mb-3 italic">Para Lojistas</h4>
              <ul className="text-sm text-gray-600 space-y-3 font-medium">
                <li className="flex gap-2"><span>1.</span> Cadastre sua loja gratuitamente.</li>
                <li className="flex gap-2"><span>2.</span> Suba fotos dos seus melhores produtos.</li>
                <li className="flex gap-2"><span>3.</span> Receba leads qualificados direto no seu celular.</li>
              </ul>
            </div>
          </div>
          <div className="text-center p-6 bg-brand-navy rounded-3xl text-white">
             <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-1">Dica de Ouro</p>
             <p className="text-xs font-medium opacity-70 italic">"Lojas com o Selo Verificado aparecem primeiro e recebem até 3x mais cliques."</p>
          </div>
        </div>
      )
    }
  }[type];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-brand-navy/90 backdrop-blur-md" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl rounded-[3.5rem] shadow-2xl animate-in zoom-in duration-300 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-10 border-b border-gray-50 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black text-brand-orange uppercase tracking-[0.3em] mb-1">{content.subtitle}</p>
              <h3 className="text-3xl font-title font-black text-brand-navy uppercase italic tracking-tighter leading-none italic">{content.title}</h3>
            </div>
            <button onClick={onClose} className="p-4 bg-gray-50 text-gray-300 hover:text-brand-navy rounded-2xl transition-all">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
        
        <div className="p-10 overflow-y-auto no-scrollbar flex-1">
          {content.body}
        </div>

        <div className="p-8 bg-gray-50 text-center shrink-0">
          <button onClick={onClose} className="bg-brand-navy text-white font-black px-12 py-5 rounded-2xl uppercase text-[10px] tracking-widest shadow-xl hover:scale-105 transition-all">
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default InfoModal;
